/**
 * Country + region for the billing address, and nothing else.
 *
 * Checkout refuses to create a billing subscription without a province and a
 * country, because Helcim computes recurring tax from the customer record and a
 * subscription created without them renews untaxed forever — silently.
 *
 * Only these two fields are collected. HelcimPay already asks for the street
 * address and postal code inside its iframe and AVS-checks them (its success
 * payload reports `avsResponse`), but it returns neither value to us, so there
 * is nothing to reuse and nothing to derive a region from. Asking again for
 * what Helcim already has would be redundant; asking for these two is the
 * minimum that makes the subscription tax-correct.
 */

export interface BillingLocation {
  country: string;
  province: string;
}

/**
 * The full address Helcim's customer record requires.
 *
 * Every field below street2 is mandatory: Helcim rejects a blank as hard as an
 * absence ("must be a valid Non-empty String"), so a partial address cannot be
 * padded into an acceptable one and is refused outright by checkout. HelcimPay
 * asks for the street and postal code inside its iframe for card AVS, but never
 * attaches them to the customer and never returns them, so there is nothing to
 * reuse — the whole address has to be collected here.
 */
export interface BillingAddress extends BillingLocation {
  name?: string;
  street1: string;
  street2?: string;
  city: string;
  postalCode: string;
}

export interface Region {
  code: string;
  name: string;
}

/**
 * Every ISO 3166-1 country, by its alpha-3 code — the form Helcim's customer
 * address documents ("3-letter abbreviation of the country (CAN, USA)").
 *
 * Generated from the iso-codes project's `iso_3166-1.json`, using each entry's
 * common name where it has one ("Bolivia", not "Bolivia, Plurinational State
 * of"), sorted accent-insensitively so Åland files under A.
 *
 * The list is exhaustive on purpose. It previously held USA, CAN and a literal
 * "OTHER" as an escape hatch for everyone else — but "OTHER" is not a country
 * code, so it went to Helcim verbatim and an international customer could not
 * be stored correctly. Helcim only computes sub-national tax for the US and
 * Canada; every other country simply records its region and is taxed at the
 * national level, which is the correct outcome.
 *
 * ISO-2 would be actively dangerous here rather than merely wrong: "CA" is
 * Canada as a country and California as a US state, so a Canadian customer sent
 * as "CA" is indistinguishable from a Californian one — and this value decides
 * what sales tax they are charged on every renewal.
 */
export const COUNTRIES: Region[] = [
  { code: "AFG", name: "Afghanistan" },
  { code: "ALA", name: "Åland Islands" },
  { code: "ALB", name: "Albania" },
  { code: "DZA", name: "Algeria" },
  { code: "ASM", name: "American Samoa" },
  { code: "AND", name: "Andorra" },
  { code: "AGO", name: "Angola" },
  { code: "AIA", name: "Anguilla" },
  { code: "ATA", name: "Antarctica" },
  { code: "ATG", name: "Antigua and Barbuda" },
  { code: "ARG", name: "Argentina" },
  { code: "ARM", name: "Armenia" },
  { code: "ABW", name: "Aruba" },
  { code: "AUS", name: "Australia" },
  { code: "AUT", name: "Austria" },
  { code: "AZE", name: "Azerbaijan" },
  { code: "BHS", name: "Bahamas" },
  { code: "BHR", name: "Bahrain" },
  { code: "BGD", name: "Bangladesh" },
  { code: "BRB", name: "Barbados" },
  { code: "BLR", name: "Belarus" },
  { code: "BEL", name: "Belgium" },
  { code: "BLZ", name: "Belize" },
  { code: "BEN", name: "Benin" },
  { code: "BMU", name: "Bermuda" },
  { code: "BTN", name: "Bhutan" },
  { code: "BOL", name: "Bolivia" },
  { code: "BES", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BIH", name: "Bosnia and Herzegovina" },
  { code: "BWA", name: "Botswana" },
  { code: "BVT", name: "Bouvet Island" },
  { code: "BRA", name: "Brazil" },
  { code: "IOT", name: "British Indian Ocean Territory" },
  { code: "BRN", name: "Brunei Darussalam" },
  { code: "BGR", name: "Bulgaria" },
  { code: "BFA", name: "Burkina Faso" },
  { code: "BDI", name: "Burundi" },
  { code: "CPV", name: "Cabo Verde" },
  { code: "KHM", name: "Cambodia" },
  { code: "CMR", name: "Cameroon" },
  { code: "CAN", name: "Canada" },
  { code: "CYM", name: "Cayman Islands" },
  { code: "CAF", name: "Central African Republic" },
  { code: "TCD", name: "Chad" },
  { code: "CHL", name: "Chile" },
  { code: "CHN", name: "China" },
  { code: "CXR", name: "Christmas Island" },
  { code: "CCK", name: "Cocos (Keeling) Islands" },
  { code: "COL", name: "Colombia" },
  { code: "COM", name: "Comoros" },
  { code: "COG", name: "Congo" },
  { code: "COD", name: "Congo, The Democratic Republic of the" },
  { code: "COK", name: "Cook Islands" },
  { code: "CRI", name: "Costa Rica" },
  { code: "CIV", name: "Côte d'Ivoire" },
  { code: "HRV", name: "Croatia" },
  { code: "CUB", name: "Cuba" },
  { code: "CUW", name: "Curaçao" },
  { code: "CYP", name: "Cyprus" },
  { code: "CZE", name: "Czechia" },
  { code: "DNK", name: "Denmark" },
  { code: "DJI", name: "Djibouti" },
  { code: "DMA", name: "Dominica" },
  { code: "DOM", name: "Dominican Republic" },
  { code: "ECU", name: "Ecuador" },
  { code: "EGY", name: "Egypt" },
  { code: "SLV", name: "El Salvador" },
  { code: "GNQ", name: "Equatorial Guinea" },
  { code: "ERI", name: "Eritrea" },
  { code: "EST", name: "Estonia" },
  { code: "SWZ", name: "Eswatini" },
  { code: "ETH", name: "Ethiopia" },
  { code: "FLK", name: "Falkland Islands (Malvinas)" },
  { code: "FRO", name: "Faroe Islands" },
  { code: "FJI", name: "Fiji" },
  { code: "FIN", name: "Finland" },
  { code: "FRA", name: "France" },
  { code: "GUF", name: "French Guiana" },
  { code: "PYF", name: "French Polynesia" },
  { code: "ATF", name: "French Southern Territories" },
  { code: "GAB", name: "Gabon" },
  { code: "GMB", name: "Gambia" },
  { code: "GEO", name: "Georgia" },
  { code: "DEU", name: "Germany" },
  { code: "GHA", name: "Ghana" },
  { code: "GIB", name: "Gibraltar" },
  { code: "GRC", name: "Greece" },
  { code: "GRL", name: "Greenland" },
  { code: "GRD", name: "Grenada" },
  { code: "GLP", name: "Guadeloupe" },
  { code: "GUM", name: "Guam" },
  { code: "GTM", name: "Guatemala" },
  { code: "GGY", name: "Guernsey" },
  { code: "GIN", name: "Guinea" },
  { code: "GNB", name: "Guinea-Bissau" },
  { code: "GUY", name: "Guyana" },
  { code: "HTI", name: "Haiti" },
  { code: "HMD", name: "Heard Island and McDonald Islands" },
  { code: "VAT", name: "Holy See (Vatican City State)" },
  { code: "HND", name: "Honduras" },
  { code: "HKG", name: "Hong Kong" },
  { code: "HUN", name: "Hungary" },
  { code: "ISL", name: "Iceland" },
  { code: "IND", name: "India" },
  { code: "IDN", name: "Indonesia" },
  { code: "IRN", name: "Iran" },
  { code: "IRQ", name: "Iraq" },
  { code: "IRL", name: "Ireland" },
  { code: "IMN", name: "Isle of Man" },
  { code: "ISR", name: "Israel" },
  { code: "ITA", name: "Italy" },
  { code: "JAM", name: "Jamaica" },
  { code: "JPN", name: "Japan" },
  { code: "JEY", name: "Jersey" },
  { code: "JOR", name: "Jordan" },
  { code: "KAZ", name: "Kazakhstan" },
  { code: "KEN", name: "Kenya" },
  { code: "KIR", name: "Kiribati" },
  { code: "KWT", name: "Kuwait" },
  { code: "KGZ", name: "Kyrgyzstan" },
  { code: "LAO", name: "Laos" },
  { code: "LVA", name: "Latvia" },
  { code: "LBN", name: "Lebanon" },
  { code: "LSO", name: "Lesotho" },
  { code: "LBR", name: "Liberia" },
  { code: "LBY", name: "Libya" },
  { code: "LIE", name: "Liechtenstein" },
  { code: "LTU", name: "Lithuania" },
  { code: "LUX", name: "Luxembourg" },
  { code: "MAC", name: "Macao" },
  { code: "MDG", name: "Madagascar" },
  { code: "MWI", name: "Malawi" },
  { code: "MYS", name: "Malaysia" },
  { code: "MDV", name: "Maldives" },
  { code: "MLI", name: "Mali" },
  { code: "MLT", name: "Malta" },
  { code: "MHL", name: "Marshall Islands" },
  { code: "MTQ", name: "Martinique" },
  { code: "MRT", name: "Mauritania" },
  { code: "MUS", name: "Mauritius" },
  { code: "MYT", name: "Mayotte" },
  { code: "MEX", name: "Mexico" },
  { code: "FSM", name: "Micronesia, Federated States of" },
  { code: "MDA", name: "Moldova" },
  { code: "MCO", name: "Monaco" },
  { code: "MNG", name: "Mongolia" },
  { code: "MNE", name: "Montenegro" },
  { code: "MSR", name: "Montserrat" },
  { code: "MAR", name: "Morocco" },
  { code: "MOZ", name: "Mozambique" },
  { code: "MMR", name: "Myanmar" },
  { code: "NAM", name: "Namibia" },
  { code: "NRU", name: "Nauru" },
  { code: "NPL", name: "Nepal" },
  { code: "NLD", name: "Netherlands" },
  { code: "NCL", name: "New Caledonia" },
  { code: "NZL", name: "New Zealand" },
  { code: "NIC", name: "Nicaragua" },
  { code: "NER", name: "Niger" },
  { code: "NGA", name: "Nigeria" },
  { code: "NIU", name: "Niue" },
  { code: "NFK", name: "Norfolk Island" },
  { code: "PRK", name: "North Korea" },
  { code: "MKD", name: "North Macedonia" },
  { code: "MNP", name: "Northern Mariana Islands" },
  { code: "NOR", name: "Norway" },
  { code: "OMN", name: "Oman" },
  { code: "PAK", name: "Pakistan" },
  { code: "PLW", name: "Palau" },
  { code: "PSE", name: "Palestine, State of" },
  { code: "PAN", name: "Panama" },
  { code: "PNG", name: "Papua New Guinea" },
  { code: "PRY", name: "Paraguay" },
  { code: "PER", name: "Peru" },
  { code: "PHL", name: "Philippines" },
  { code: "PCN", name: "Pitcairn" },
  { code: "POL", name: "Poland" },
  { code: "PRT", name: "Portugal" },
  { code: "PRI", name: "Puerto Rico" },
  { code: "QAT", name: "Qatar" },
  { code: "REU", name: "Réunion" },
  { code: "ROU", name: "Romania" },
  { code: "RUS", name: "Russian Federation" },
  { code: "RWA", name: "Rwanda" },
  { code: "BLM", name: "Saint Barthélemy" },
  { code: "SHN", name: "Saint Helena, Ascension and Tristan da Cunha" },
  { code: "KNA", name: "Saint Kitts and Nevis" },
  { code: "LCA", name: "Saint Lucia" },
  { code: "MAF", name: "Saint Martin (French part)" },
  { code: "SPM", name: "Saint Pierre and Miquelon" },
  { code: "VCT", name: "Saint Vincent and the Grenadines" },
  { code: "WSM", name: "Samoa" },
  { code: "SMR", name: "San Marino" },
  { code: "STP", name: "Sao Tome and Principe" },
  { code: "SAU", name: "Saudi Arabia" },
  { code: "SEN", name: "Senegal" },
  { code: "SRB", name: "Serbia" },
  { code: "SYC", name: "Seychelles" },
  { code: "SLE", name: "Sierra Leone" },
  { code: "SGP", name: "Singapore" },
  { code: "SXM", name: "Sint Maarten (Dutch part)" },
  { code: "SVK", name: "Slovakia" },
  { code: "SVN", name: "Slovenia" },
  { code: "SLB", name: "Solomon Islands" },
  { code: "SOM", name: "Somalia" },
  { code: "ZAF", name: "South Africa" },
  { code: "SGS", name: "South Georgia and the South Sandwich Islands" },
  { code: "KOR", name: "South Korea" },
  { code: "SSD", name: "South Sudan" },
  { code: "ESP", name: "Spain" },
  { code: "LKA", name: "Sri Lanka" },
  { code: "SDN", name: "Sudan" },
  { code: "SUR", name: "Suriname" },
  { code: "SJM", name: "Svalbard and Jan Mayen" },
  { code: "SWE", name: "Sweden" },
  { code: "CHE", name: "Switzerland" },
  { code: "SYR", name: "Syria" },
  { code: "TWN", name: "Taiwan" },
  { code: "TJK", name: "Tajikistan" },
  { code: "TZA", name: "Tanzania" },
  { code: "THA", name: "Thailand" },
  { code: "TLS", name: "Timor-Leste" },
  { code: "TGO", name: "Togo" },
  { code: "TKL", name: "Tokelau" },
  { code: "TON", name: "Tonga" },
  { code: "TTO", name: "Trinidad and Tobago" },
  { code: "TUN", name: "Tunisia" },
  { code: "TUR", name: "Türkiye" },
  { code: "TKM", name: "Turkmenistan" },
  { code: "TCA", name: "Turks and Caicos Islands" },
  { code: "TUV", name: "Tuvalu" },
  { code: "UGA", name: "Uganda" },
  { code: "UKR", name: "Ukraine" },
  { code: "ARE", name: "United Arab Emirates" },
  { code: "GBR", name: "United Kingdom" },
  { code: "USA", name: "United States" },
  { code: "UMI", name: "United States Minor Outlying Islands" },
  { code: "URY", name: "Uruguay" },
  { code: "UZB", name: "Uzbekistan" },
  { code: "VUT", name: "Vanuatu" },
  { code: "VEN", name: "Venezuela" },
  { code: "VNM", name: "Vietnam" },
  { code: "VGB", name: "Virgin Islands, British" },
  { code: "VIR", name: "Virgin Islands, U.S." },
  { code: "WLF", name: "Wallis and Futuna" },
  { code: "ESH", name: "Western Sahara" },
  { code: "YEM", name: "Yemen" },
  { code: "ZMB", name: "Zambia" },
  { code: "ZWE", name: "Zimbabwe" },
];

/** Fast membership test for the picker's own values. */
const COUNTRY_CODES = new Set(COUNTRIES.map((c) => c.code));

/** Whether a code is a country we can actually store against a customer. */
export function isKnownCountry(country: string): boolean {
  return COUNTRY_CODES.has(country);
}

/**
 * ISO-2 codes this app itself wrote before the move to Helcim's three-letter
 * form. An address saved under the old scheme is still sitting in checkout, so
 * it comes back on prefill and would otherwise be forwarded verbatim — Helcim
 * would reject it, or worse read "CA" as California. The mapping is unambiguous
 * because the old picker only ever offered these two as countries.
 *
 * A Map, not an object literal: the country reaches here from the client, and
 * an object lookup would answer "constructor" or "toString" with something
 * inherited from Object.prototype instead of leaving the value alone.
 */
const LEGACY_COUNTRY_CODES = new Map([
  ["US", "USA"],
  ["CA", "CAN"],
]);

/** A stored country in the form Helcim accepts. Non-legacy values pass through. */
export function canonicalCountry(country: string): string {
  return LEGACY_COUNTRY_CODES.get(country) ?? country;
}

export const US_STATES: Region[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

export const CA_PROVINCES: Region[] = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

/** Regions to offer for a country, or null when the region is free text. */
export function regionsFor(country: string): Region[] | null {
  const code = canonicalCountry(country);
  if (code === "USA") return US_STATES;
  if (code === "CAN") return CA_PROVINCES;
  return null;
}

/** The field label for a country's sub-national division. */
export function regionLabel(country: string): string {
  const code = canonicalCountry(country);
  if (code === "USA") return "State";
  if (code === "CAN") return "Province";
  return "Region";
}

/**
 * Whether a location satisfies checkout's province + country requirement.
 *
 * Mirrors checkout's own `hasTaxableLocation`, which tests both fields for
 * truthiness — so a value that is only whitespace has to fail here too, or the
 * request is rejected server-side after the card has already been tokenized.
 */
export function isCompleteBillingLocation(
  location: Partial<BillingLocation> | null | undefined,
): location is BillingLocation {
  return Boolean(location?.country?.trim() && location?.province?.trim());
}

/** Whether every field Helcim insists on is present and non-blank. */
export function isCompleteBillingAddress(
  addr: Partial<BillingAddress> | null | undefined,
): addr is BillingAddress {
  return Boolean(
    addr?.street1?.trim() &&
    addr?.city?.trim() &&
    addr?.postalCode?.trim() &&
    isCompleteBillingLocation(addr),
  );
}

/**
 * Normalize a full address for submission. Returns null unless every required
 * field survives trimming and the region is real for its country — checkout
 * rejects anything less, and a bad region would be recorded as a tax
 * jurisdiction.
 */
export function normalizeBillingAddress(
  addr: Partial<BillingAddress> | null | undefined,
): BillingAddress | null {
  const location = normalizeBillingLocation(addr);
  if (!location || !isCompleteBillingAddress(addr)) return null;
  const street2 = addr.street2?.trim();
  return {
    ...location,
    ...(addr.name?.trim() ? { name: addr.name.trim() } : {}),
    street1: addr.street1.trim(),
    ...(street2 ? { street2 } : {}),
    city: addr.city.trim(),
    postalCode: addr.postalCode.trim(),
  };
}

/**
 * Normalize for submission: trimmed, and with a US/CA region validated against
 * its list. An unknown code for a country whose regions we enumerate is a bug
 * on our side rather than something to forward to Helcim as a tax jurisdiction.
 */
export function normalizeBillingLocation(
  location: Partial<BillingLocation> | null | undefined,
): BillingLocation | null {
  const raw = location?.country?.trim() ?? "";
  const province = location?.province?.trim() ?? "";
  if (!raw || !province) return null;

  // Canonicalize before validating, so a legacy ISO-2 country is checked
  // against the right region list AND leaves here in Helcim's form. This is the
  // last point before the value reaches checkout, so nothing may pass through
  // un-canonicalized, and nothing the picker never offered may pass at all.
  const country = canonicalCountry(raw);
  if (!isKnownCountry(country)) return null;

  const known = regionsFor(country);
  if (known && !known.some((r) => r.code === province)) return null;

  return { country, province };
}
